import { applyVideoAssetsReadyEvent } from '../utils/videoPreviewProjection';
import { segmentCache } from './segmentCache';
import { resultRevisionKey } from '../utils/resultRevisions';
import {
  apiService,
  ProcessingStreamEvent,
  ProcessingProgressScope,
  ProgressMessage,
  ProgressConnectionState,
  PromptRun,
  ImportJob,
  PromptRunProcessingStatus,
  Video,
} from './api';

type ProcessingListener = (event: ProcessingStreamEvent) => void;

interface VideoRealtimeState {
  video_id: string;
  status?: string;
  thumbnail_gcs_uri?: string | null;
  gif_gcs_uri?: string | null;
  processing_status?: PromptRunProcessingStatus[];
  revision: number;
  updated_at: number;
}

export const applyRealtimeStateToVideo = (
  video: Video,
  realtimeState?: VideoRealtimeState
): Video => {
  if (!realtimeState) {
    return video;
  }

  if (realtimeState.thumbnail_gcs_uri !== undefined || realtimeState.gif_gcs_uri !== undefined) {
    video = applyVideoAssetsReadyEvent(video, {
      event_type: 'video.assets_ready', video_id: video.video_id,
      payload: { video: {
        ...(realtimeState.thumbnail_gcs_uri !== undefined ? { thumbnail_gcs_uri: realtimeState.thumbnail_gcs_uri } : {}),
        ...(realtimeState.gif_gcs_uri !== undefined ? { gif_gcs_uri: realtimeState.gif_gcs_uri } : {}),
      } },
    });
  }

  let changed = false;
  let nextStatus = video.status;
  let nextProcessingStatus = video.processing_status || [];

  if (realtimeState.status && realtimeState.status !== nextStatus) {
    nextStatus = realtimeState.status;
    changed = true;
  }

  if (Array.isArray(realtimeState.processing_status)) {
    const incomingIds = new Set(realtimeState.processing_status.map((run) => run.run_id));
    const mergedProcessingStatus = [
      ...(video.processing_status || []).filter((run) => !incomingIds.has(run.run_id)),
      ...realtimeState.processing_status,
    ];
    const serializedCurrent = JSON.stringify(video.processing_status || []);
    const serializedNext = JSON.stringify(mergedProcessingStatus);
    if (serializedCurrent !== serializedNext) {
      nextProcessingStatus = mergedProcessingStatus;
      changed = true;
    }
  }

  if (!changed) {
    return video;
  }

  return {
    ...video,
    status: nextStatus,
    processing_status: nextProcessingStatus,
  };
};

export class ProcessingRealtimeStore {
  private listeners = new Map<ProcessingListener, { scope: ProcessingProgressScope; connection?: (state: ProgressConnectionState) => void }>();
  private streamStopper: (() => void) | null = null;
  private reconcileTimer: ReturnType<typeof setTimeout> | null = null;
  private videoStates = new Map<string, VideoRealtimeState>();
  private snapshotReady = false;
  private hasBootstrapped = false;
  private authGeneration = 0;
  private importedRevisions = new Map<string, unknown>();
  private assetRequests = new Map<string, Promise<void>>();
  private importOutputRequests = new Map<string, Promise<void>>();
  private records = new Map<string, { version: string; epoch: string; state: Record<string, any> }>();
  private connectionListeners = new Set<() => void>();
  private streamGeneration = 0;
  private epoch: string | null = null;
  private streamSuspended = false;
  private connection: ProgressConnectionState = 'stopped';
  private scopeKey = '';
  private lifecycleAttached = false;
  private forceReconcile = false;

  private setConnection = (state: ProgressConnectionState) => {
    const changed = this.connection !== state;
    this.connection = state;
    this.listeners.forEach((entry) => entry.connection?.(state));
    if (changed) this.connectionListeners.forEach((listener) => listener());
  };

  private emit(event: ProcessingStreamEvent) {
    this.listeners.forEach((_entry, listener) => listener(event));
  }

  private eventFor(kind: string, id: string, state: Record<string, any>): ProcessingStreamEvent {
    const prefix = kind === 'video' ? 'video' : kind === 'run' ? 'run' : kind;
    return {
      event_type: `${prefix}.progress`,
      video_id: kind === 'video' ? id : undefined,
      run_id: kind === 'run' ? id : state.selected_run_id || undefined,
      index_id: state.target?.index_id || state.target_index_id || state.run_context?.index_id,
      created_at: state.updated_at,
      payload: { ...state, snapshot: state },
    };
  }

  private onMessage = (message: ProgressMessage) => {
    if (message.type === 'progress.reset') { this.epoch = message.epoch; this.snapshotReady = false; return; }
    if (message.epoch !== this.epoch) return;
    if (message.type === 'progress.ready') { this.snapshotReady = true; this.hasBootstrapped = true; return; }
    if (message.type === 'progress.remove' && message.kind && message.id) {
      this.records.delete(`${message.kind}:${message.id}`);
      if (message.kind === 'video') this.videoStates.delete(message.id);
      this.emit({ event_type: 'progress.remove', payload: { kind: message.kind, id: message.id } });
      return;
    }
    if (message.type !== 'progress.upsert' || !message.kind || !message.id || !message.version || !message.state) return;
    const key = `${message.kind}:${message.id}`;
    const previous = this.records.get(key);
    // Versions are compared per document, never as a global event cursor.
    const compareVersion = (a: string, b: string) => {
      const [as, an = '0'] = a.split('.'); const [bs, bn = '0'] = b.split('.');
      return Number(as) - Number(bs) || Number(an) - Number(bn);
    };
    if (previous) {
      const order = compareVersion(message.version, previous.version);
      if (order < 0 || (order === 0 && previous.epoch === message.epoch && JSON.stringify(previous.state) === JSON.stringify(message.state))) return;
    }
    this.records.delete(key);
    this.records.set(key, { version: message.version, epoch: message.epoch, state: message.state });
    if (message.kind === 'video') {
      for (const run of message.state.processing_status || []) {
        const old = previous?.state.processing_status?.find((entry: any) => entry.run_id === run.run_id);
        if (JSON.stringify(old?.result_revisions || {}) !== JSON.stringify(run.result_revisions || {})) {
          segmentCache.observeResultRevision(message.id, run.run_id, resultRevisionKey(run.result_revisions, ['metadata', 'segments', 'assets', 'image_index']));
          if (previous && old?.result_revisions?.assets !== run.result_revisions?.assets) this.refreshVideoAssets(message.id);
        }
      }
      const current = this.videoStates.get(message.id);
      this.videoStates.set(message.id, {
        ...message.state, video_id: message.id,
        revision: (current?.revision || 0) + 1, updated_at: Date.now(),
      });
    }
    if (message.kind === 'import_job' && message.state.outputs_revision > 0
      && message.state.outputs_revision !== this.importedRevisions.get(message.id)
      && (previous || this.snapshotReady || this.hasBootstrapped || !['completed', 'failed', 'cancelled'].includes(message.state.status))) {
      this.refreshImportOutputs(message.id);
    }
    // Retain only a bounded cache across navigation; active data is at the end.
    if (this.records.size > 1000) {
      const oldest = this.records.keys().next().value as string;
      this.records.delete(oldest);
      if (oldest.startsWith('video:')) this.videoStates.delete(oldest.slice(6));
    }
    this.emit(this.eventFor(message.kind, message.id, message.state));
  };

  private refreshVideoAssets(videoId: string) {
    if (this.assetRequests.has(videoId)) return;
    const generation = this.authGeneration;
    const revision = () => JSON.stringify(this.records.get(`video:${videoId}`)?.state.processing_status?.map((run: any) => run.result_revisions?.assets));
    const load = async () => {
      let requested: string;
      do {
        requested = revision();
        const videos = await apiService.getBatchVideoSummaries([videoId]);
        if (generation !== this.authGeneration) return;
        videos.forEach((video) => this.emit({ event_type: 'video.assets_ready', video_id: videoId, payload: { video } }));
      } while (generation === this.authGeneration && requested !== revision());
    };
    this.assetRequests.set(videoId, load().catch(() => undefined).finally(() => this.assetRequests.delete(videoId)));
  }

  private refreshImportOutputs(jobId: string) {
    if (this.importOutputRequests.has(jobId)) return;
    const generation = this.authGeneration;
    const load = async () => {
      let revision: unknown;
      do {
        const state = this.records.get(`import_job:${jobId}`)?.state;
        revision = state?.outputs_revision;
        const outputs = await apiService.getAllImportJobOutputs(jobId);
        const ids = Array.from(new Set(outputs.flatMap((output) => output.video_id ? [output.video_id] : [])));
        for (let offset = 0; offset < ids.length; offset += 100) {
          const videos = await apiService.getBatchVideoSummaries(ids.slice(offset, offset + 100));
          if (generation !== this.authGeneration) return;
          videos.forEach((video) => this.emit({ event_type: 'import_job.video_imported',
            index_id: state?.target_index_id || undefined, video_id: video.video_id,
            payload: { video, target_index_id: state?.target_index_id || null } }));
        }
        this.importedRevisions.set(jobId, revision);
      } while (generation === this.authGeneration && revision !== this.records.get(`import_job:${jobId}`)?.state.outputs_revision);
    };
    const pending = load().catch(() => undefined).finally(() => this.importOutputRequests.delete(jobId));
    this.importOutputRequests.set(jobId, pending);
  }

  private combinedScope(): ProcessingProgressScope | null {
    if (!this.listeners.size) return null;
    const scopes = Array.from(this.listeners.values()).map((entry) => entry.scope);
    const scope: ProcessingProgressScope = { discover: true };
    const index = scopes.find((entry) => entry.index_id)?.index_id;
    if (index) scope.index_id = index;
    (['video_ids', 'run_ids', 'import_job_ids', 'export_ids'] as const).forEach((key) => {
      // Focused modal/upload scopes take precedence over a full paginated grid.
      const ids = Array.from(new Set([...scopes].sort((a, b) => (a[key]?.length || 0) - (b[key]?.length || 0))
        .flatMap((entry) => entry[key] || []))).filter(Boolean);
      // Views are paginated to 100; never open an unbounded account watch.
      if (ids.length) scope[key] = ids.slice(0, 100).sort();
    });
    return Object.keys(scope).length ? scope : null;
  }

  private reconcile = (force = false) => {
    const scope = this.combinedScope();
    const hidden = typeof document !== 'undefined' && document.visibilityState === 'hidden';
    const offline = typeof navigator !== 'undefined' && navigator.onLine === false;
    if (this.streamSuspended || hidden || offline || !scope) {
      this.stopStream();
      this.setConnection(offline ? 'unavailable' : 'stopped');
      return;
    }
    const key = JSON.stringify(scope);
    if (!force && key === this.scopeKey && (this.streamStopper || this.connection === 'idle')) return;
    this.stopStream();
    this.scopeKey = key;
    const generation = this.streamGeneration;
    try { this.streamStopper = apiService.subscribeProcessingProgress(scope,
      (message) => { if (generation === this.streamGeneration) this.onMessage(message); },
      (state) => { if (generation === this.streamGeneration) this.setConnection(state); },
      (reason) => {
        if (generation !== this.streamGeneration) return;
        this.streamStopper = null;
        this.setConnection(reason === 'idle' ? 'idle' : reason === 'error' ? 'unavailable' : 'stopped');
      }); } catch { this.setConnection('unavailable'); }
  };

  private scheduleReconcile(force = false) {
    this.forceReconcile = this.forceReconcile || force;
    if (this.reconcileTimer) clearTimeout(this.reconcileTimer);
    this.reconcileTimer = setTimeout(() => {
      this.reconcileTimer = null;
      const refresh = this.forceReconcile; this.forceReconcile = false;
      this.reconcile(refresh);
    }, 100);
  }

  private onVisibility = () => {
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') this.reconcile();
    else this.scheduleReconcile(true);
  };
  private onOnline = () => this.scheduleReconcile(true);

  private attachLifecycle() {
    if (this.lifecycleAttached || typeof window === 'undefined') return;
    this.lifecycleAttached = true;
    document.addEventListener('visibilitychange', this.onVisibility);
    window.addEventListener('online', this.onOnline);
    window.addEventListener('offline', this.onVisibility);
    window.addEventListener('focus', this.onOnline);
  }

  private detachLifecycle() {
    if (!this.lifecycleAttached) return;
    this.lifecycleAttached = false;
    document.removeEventListener('visibilitychange', this.onVisibility);
    window.removeEventListener('online', this.onOnline);
    window.removeEventListener('offline', this.onVisibility);
    window.removeEventListener('focus', this.onOnline);
  }

  private stopStream() {
    this.streamGeneration += 1;
    const stop = this.streamStopper;
    this.streamStopper = null;
    this.epoch = null;
    stop?.();
  }

  subscribe(listener: ProcessingListener, scope: ProcessingProgressScope = {}, connection?: (state: ProgressConnectionState) => void): () => void {
    if (this.streamSuspended) return () => undefined;
    this.listeners.set(listener, { scope, connection });
    this.attachLifecycle();
    connection?.(this.connection);
    // New views must receive cached authoritative records even when a fresh
    // listener snapshot has the same Firestore version. Defer to avoid a
    // synchronous notification reentering a subscriber's setup/cleanup.
    const generation = this.streamGeneration;
    void Promise.resolve().then(() => {
      if (!this.listeners.has(listener) || generation !== this.streamGeneration) return;
      this.records.forEach(({ state }, key) => {
        const separator = key.indexOf(':');
        const kind = key.slice(0, separator); const id = key.slice(separator + 1);
        const ids = kind === 'video' ? scope.video_ids : kind === 'run' ? scope.run_ids
          : kind === 'import_job' ? scope.import_job_ids : scope.export_ids;
        const indexId = state.target?.index_id || state.target_index_id || state.run_context?.index_id;
        if (scope.discover || ids?.includes(id) || (scope.index_id && indexId === scope.index_id)) listener(this.eventFor(kind, id, state));
      });
    });
    this.scheduleReconcile();
    return () => {
      this.listeners.delete(listener);
      if (!this.listeners.size) {
        if (this.reconcileTimer) clearTimeout(this.reconcileTimer);
        this.reconcileTimer = null;
        this.stopStream(); this.detachLifecycle(); this.scopeKey = '';
        this.setConnection('stopped');
      } else this.scheduleReconcile();
    };
  }

  publishImportedVideos(indexId: string | null, videos: Video[]): void {
    videos.forEach((video) => this.emit({ event_type: 'import_job.video_imported', index_id: indexId || undefined,
      video_id: video.video_id, payload: { video, target_index_id: indexId } }));
    this.refresh();
  }
  refresh(): void { this.scheduleReconcile(true); }
  getConnectionState(): ProgressConnectionState { return this.connection; }
  subscribeConnection = (listener: () => void) => {
    this.connectionListeners.add(listener);
    return () => { this.connectionListeners.delete(listener); };
  };
  getVideoState(videoId: string): VideoRealtimeState | undefined { return this.videoStates.get(videoId); }
  getRun(runId: string): Partial<PromptRun> | undefined { return this.records.get(`run:${runId}`)?.state; }
  getImportJob(jobId: string): Partial<ImportJob> | undefined { return this.records.get(`import_job:${jobId}`)?.state; }

  clearCachedState(): void { this.importedRevisions.clear(); this.assetRequests.clear(); this.importOutputRequests.clear(); this.videoStates.clear(); this.records.clear(); this.scopeKey = ''; }
  resetForAuthTransition(): void {
    this.authGeneration += 1;
    this.hasBootstrapped = false;
    this.streamSuspended = true;
    if (this.reconcileTimer) clearTimeout(this.reconcileTimer);
    this.reconcileTimer = null;
    this.forceReconcile = false;
    this.stopStream(); this.listeners.clear(); this.detachLifecycle(); this.clearCachedState();
    this.setConnection('stopped');
  }
  activateUserScope(scopeId: string | null): void {
    this.resetForAuthTransition(); this.streamSuspended = scopeId === null;
  }
}

export const processingRealtimeStore = new ProcessingRealtimeStore();
